import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, X, Sparkles } from 'lucide-react';
import { haptic } from '@/lib/haptic';

const TUTORIAL_KEY = 'locomoco_tutorial_done';

interface TutorialProps {
  onClose: () => void;
}

const STEPS = [
  {
    emoji: '🧬',
    title: 'locomocoへようこそ',
    body: 'DNAの螺旋のように、同じ単語を何度も繰り返すことで長期記憶に定着させます。24週間毎日30分の学習で、GitHubのレビューやIssueで実際に使われている2400語を身につけます。',
    hint: null,
  },
  {
    emoji: null,
    title: 'カードのめくり方',
    body: 'カードをタップすると日本語の意味と例文が表示されます。音声ボタンで発音も確認できます。自動音声の再生は設定から消音にすることができます。',
    hint: 'tap',
  },
  {
    emoji: null,
    title: '3方向のスワイプ',
    body: '意味を確認したら、正直に3方向で答えてください。スワイプの結果が将来の出題頻度に影響します。',
    hint: 'swipe',
  },
  {
    emoji: null,
    title: 'スワイプが学習を変える',
    body: '左・右・上スワイプはそれぞれ異なる意味を持ち、内部の「定着スコア」が変化します。',
    hint: 'mechanism',
  },
  {
    emoji: null,
    title: '1週間の流れ',
    body: '',
    hint: 'schedule',
  },
  {
    emoji: '🔥',
    title: 'ストリークを守ろう',
    body: '毎日続けることでストリーク（連続日数）が伸びます。30分のセッションを完了するとストリークが更新されます。',
    hint: null,
  },
];

export function isTutorialDone(): boolean {
  if (typeof window === 'undefined') return true;
  return localStorage.getItem(TUTORIAL_KEY) === '1';
}

export default function Tutorial({ onClose }: TutorialProps) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  const handleNext = () => {
    if (isLast) {
      haptic([10, 30, 10]);
      localStorage.setItem(TUTORIAL_KEY, '1');
      onClose();
    } else {
      haptic(8);
      setStep((s) => s + 1);
    }
  };

  const handleSkip = () => {
    haptic(8);
    localStorage.setItem(TUTORIAL_KEY, '1');
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/80 flex flex-col items-center justify-end p-0"
      onClick={(e) => e.target === e.currentTarget && handleSkip()}
    >
      <motion.div
        key={step}
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ type: 'spring', damping: 28, stiffness: 260 }}
        className="w-full bg-white dark:bg-slate-900 rounded-t-3xl p-7 pb-safe shadow-2xl"
      >
        {/* 閉じるボタン */}
        <button
          onClick={handleSkip}
          className="absolute top-5 right-5 p-2 rounded-full text-slate-400 active:bg-slate-100 dark:active:bg-slate-800"
        >
          <X size={20} />
        </button>

        {/* ステップインジケーター */}
        <div className="flex gap-1.5 mb-6">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === step
                  ? 'bg-blue-600 flex-[2]'
                  : i < step
                  ? 'bg-blue-300 flex-1'
                  : 'bg-slate-200 dark:bg-slate-700 flex-1'
              }`}
            />
          ))}
        </div>

        {/* コンテンツ */}
        {current.hint === 'tap'       && <TapDemo />}
        {current.hint === 'swipe'     && <SwipeDemo />}
        {current.hint === 'mechanism' && <MechanismDemo />}
        {current.hint === 'schedule'  && <ScheduleDemo />}
        {current.emoji && (
          <div className="text-6xl mb-4 text-center">{current.emoji}</div>
        )}

        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-3">
          {current.title}
        </h2>
        {current.body ? (
          <p className="text-slate-500 dark:text-slate-400 text-base leading-relaxed mb-8">
            {current.body}
          </p>
        ) : (
          <div className="mb-6" />
        )}

        <button
          onClick={handleNext}
          className="w-full py-4 bg-blue-600 text-white font-black rounded-2xl text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg shadow-blue-600/25"
        >
          {isLast ? '始める' : '次へ'}
          {!isLast && <ChevronRight size={20} />}
        </button>
      </motion.div>
    </motion.div>
  );
}

/* 1週間サイクルのスケジュール解説 */
function ScheduleDemo() {
  return (
    <div className="mb-5 space-y-3">
      {/* 1〜6日目 */}
      <div className="flex items-start gap-3 p-3 rounded-2xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40">
        <div className="w-9 h-9 rounded-xl bg-blue-500 flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-blue-500/30">
          <span className="text-white text-base">📖</span>
        </div>
        <div>
          <p className="text-sm font-black text-blue-600 dark:text-blue-400">1〜6日目：インプット</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
            今週の新しい<span className="font-black">100単語</span>を毎日30分くり返し学習。スワイプで定着度を記録します。
          </p>
        </div>
      </div>

      {/* 7日目 */}
      <div className="flex items-start gap-3 p-3 rounded-2xl bg-violet-50 dark:bg-violet-950/30 border border-violet-100 dark:border-violet-900/40">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-violet-500/30">
          <span className="text-white text-base">🔄</span>
        </div>
        <div>
          <p className="text-sm font-black text-violet-600 dark:text-violet-400">7日目：スパイラル復習</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
            直近3週分の単語（最大300語）を一気に総復習。フォーカス単語も自動追加されます。
          </p>
        </div>
      </div>

      {/* 第1週例外 */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
        <span className="text-base">😴</span>
        <p className="text-[11px] text-slate-500 leading-snug">
          <span className="font-black text-slate-600 dark:text-slate-300">第1週の7日目だけはおやすみ！</span>
          {' '}何もしなくてもストリークは自動で維持されます。
        </p>
      </div>

      {/* 週別復習量 */}
      <div className="rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60">
          <p className="text-[10px] font-black text-slate-400 tracking-widest">週ごとの復習量</p>
        </div>
        {[
          { week: 1, label: '7日目', note: 'おやすみ 😴', color: 'text-slate-400' },
          { week: 2, label: '14日目', note: '第1週 100語を復習', color: 'text-blue-500' },
          { week: 3, label: '21日目', note: '第1〜2週 200語を復習', color: 'text-indigo-500' },
          { week: 4, label: '28日目', note: '第1〜3週 300語を復習', color: 'text-violet-500' },
          { week: 5, label: '35日目〜', note: '直近3週 300語のローリング 🏆', color: 'text-yellow-500' },
        ].map((row) => (
          <div key={row.week} className="flex items-center gap-2 px-3 py-2 border-t border-slate-100 dark:border-slate-800">
            <span className="text-[10px] font-black text-slate-400 w-12">第{row.week}週</span>
            <span className="text-[10px] text-slate-400">{row.label}：</span>
            <span className={`text-[10px] font-black ${row.color}`}>{row.note}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* カードタップのデモアニメーション */
function TapDemo() {
  const [flipped, setFlipped] = useState(false);

  return (
    <div className="mb-5 flex justify-center">
      <motion.div
        className="w-56 h-32 rounded-2xl cursor-pointer select-none"
        onClick={() => setFlipped((f) => !f)}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.4 }}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* 表面 */}
        <div
          className="absolute inset-0 rounded-2xl bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center shadow-md"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <span className="text-2xl font-black text-slate-900 dark:text-white">abandon</span>
          <span className="text-slate-400 text-sm mt-1">タップ →</span>
        </div>
        {/* 裏面 */}
        <div
          className="absolute inset-0 rounded-2xl bg-blue-600 flex flex-col items-center justify-center shadow-md"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <span className="text-white font-black text-xl">捨てる・断念する</span>
        </div>
      </motion.div>
    </div>
  );
}

/* スワイプ方向のデモ表示 — 実際のオーバーレイと同じ色・デザイン */
function SwipeDemo() {
  return (
    <div className="mb-5 grid grid-cols-3 gap-2.5 text-center">
      {/* 左スワイプ */}
      <div className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50">
        <div className="w-11 h-11 rounded-xl bg-red-500 flex items-center justify-center shadow-md shadow-red-500/30">
          <span className="text-white font-black text-base">←</span>
        </div>
        <p className="text-xs font-black text-red-500">また後で</p>
        <p className="text-[10px] text-slate-400 leading-tight">次ラウンドも出題</p>
      </div>
      {/* 上スワイプ（violet グラデーション） */}
      <div className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-900/50">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md shadow-violet-500/30">
          <Sparkles size={18} className="text-white" />
        </div>
        <p className="text-xs font-black text-violet-600 dark:text-violet-400">もう完璧！</p>
        <p className="text-[10px] text-slate-400 leading-tight">今日から除外</p>
      </div>
      {/* 右スワイプ */}
      <div className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50">
        <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center shadow-md shadow-emerald-500/30">
          <span className="text-white font-black text-base">→</span>
        </div>
        <p className="text-xs font-black text-emerald-600 dark:text-emerald-400">知っている</p>
        <p className="text-[10px] text-slate-400 leading-tight">次ラウンドも出題</p>
      </div>
    </div>
  );
}

/* スワイプが定着スコアに与える影響の説明 */
function MechanismDemo() {
  return (
    <div className="mb-5 space-y-2.5">
      {/* 左スワイプ */}
      <div className="flex items-start gap-3 p-3 rounded-2xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/40">
        <div className="w-9 h-9 rounded-xl bg-red-500 flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-red-500/30">
          <span className="text-white font-black text-sm">←</span>
        </div>
        <div>
          <p className="text-sm font-black text-red-600 dark:text-red-400">また後で（左）</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
            定着スコア <span className="font-black text-red-500">−10</span>。
            「知らない」という強いシグナル。7日目のフォーカス単語リスト（R）に入りやすくなる。
          </p>
        </div>
      </div>
      {/* 右スワイプ */}
      <div className="flex items-start gap-3 p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40">
        <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-emerald-500/30">
          <span className="text-white font-black text-sm">→</span>
        </div>
        <div>
          <p className="text-sm font-black text-emerald-600 dark:text-emerald-400">知っている（右）</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
            定着スコア <span className="font-black text-emerald-500">+5</span>。
            「わかった」という弱い正シグナル。次のラウンドでも出続けて定着を深める。
          </p>
        </div>
      </div>
      {/* 上スワイプ — violet グラデーション（実際の演出と統一） */}
      <div className="flex items-start gap-3 p-3 rounded-2xl bg-violet-50 dark:bg-violet-950/30 border border-violet-100 dark:border-violet-900/40">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-violet-500/30">
          <Sparkles size={15} className="text-white" />
        </div>
        <div>
          <p className="text-sm font-black text-violet-600 dark:text-violet-400">もう完璧！（上）</p>
          <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
            定着スコア <span className="font-black text-violet-500">+15</span>。
            今日のセッションから除外されるが、翌日から再び登場する。完璧でも繰り返しが大切。
          </p>
        </div>
      </div>
    </div>
  );
}
